<template>
  <v-app id="app_measurements">
    <v-container fluid>
      <v-row class="pl-2 pr-6">
        <v-col class="mt-3" cols="7">
          <p class="custom_page_main_title">AREA MANAGEMENT</p>
          <p class="custom_page_main_sub_title" style="opacity: 0.6">
            Set up a new area with a code that fits your needs.
          </p>
        </v-col>
        <v-col class="px-4 mt-5" cols="5">
          <v-row class="custom_tool_bar_style">
            <v-btn color="#800080" size="x-large" variant="text" @click="AreaBulkUploadDialog = true">
              <v-icon>mdi-file-upload-outline</v-icon>&nbsp;&nbsp;AREA BULK UPLOAD
            </v-btn>
            <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
            <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
              <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <v-card class="custom_card_box mt-8" elevation="1" border="true" rounded="lg">
        <v-card-text class="custom_tool_bar_style d-flex align-center">
          <div class="d-flex align-center flex-grow-1">
            <p class="custom_page_component_main_title ma-0">AREA LIST</p>
          </div>
          <div class="d-flex align-center">
            <v-btn class="custom_measurement_btn" variant="outlined" @click="openDialog" v-if="permissions.write">
              &nbsp;&nbsp;CREATE AREA
            </v-btn>
            <v-btn class="custom_pdf_btn ml-4" variant="outlined" @click="exportPDF">
              <v-icon size="large">mdi-file-pdf</v-icon>&nbsp;&nbsp;PDF
            </v-btn>
            <v-btn class="custom_excel_btn ml-4" variant="outlined" @click="exportExcel">
              <v-icon size="large">mdi-file-excel</v-icon>&nbsp;&nbsp;EXCEL
            </v-btn>
          </div>
        </v-card-text>

        <v-divider class="my-2" style="height: 2px"></v-divider>

        <v-row align="center" justify="space-between" class="pa-4">
          <v-col cols="auto">
            <v-card elevation="1" class="pa-3 d-flex align-center">
              <div>
                <h1 class="custom_number_of_measurement_btn">{{ totalAreas }}</h1>
                <p class="font-weight-medium">TOTAL AREAS</p>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field v-model="search" append-inner-icon="mdi-magnify" clearable hide-details label="Search"
              placeholder="Search Areas" variant="outlined" class="custom_input_field_color"
              density="comfortable"></v-text-field>
          </v-col>
        </v-row>

        <v-divider class="my-2" style="height: 2px"></v-divider>

        <v-data-table 
          ref="dataTable"
          :headers="headers" 
          :items="filteredAreas" 
          :search="search"
          :items-per-page="itemsPerPage"
          :page="page"
          @update:page="page = $event"
          @update:items-per-page="itemsPerPage = $event"
          class="elevation-1 custom_table_normal"
        >
          <template v-slot:[`item.description`]="{ item }">
            <span v-if="item.description && item.description.length" v-html="getSanitizedHTML(item.description)"></span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-menu transition="slide-x-transition" id="app_product_list">
              <template v-slot:activator="{ props }">
                <v-btn style="background-color: #800080; color: white;" variant="flat" size="large" v-bind="props">
                  ACTIONS &nbsp; &nbsp;<v-icon>mdi-menu-down</v-icon>
                </v-btn>
              </template>
                <v-list>
                  <v-list-item v-if="permissions.write" @click="editArea(item)">
                    <v-list-item-title style="color: blue;">
                      EDIT
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item v-if="permissions.delete" @click="confirmDelete(item)">
                    <v-list-item-title style="color: red;">
                      DELETE
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog v-model="dialog" max-height="700" id="app_lead_list" max-width="600">
        <v-card rounded="lg" max-width="700" width="100%" class="mx-auto">
          <v-card color="#F0F0F0" flat class="px-4 py-2">
            <div class="d-flex align-center justify-space-between">
              <v-card-title class="text-subtitle-1 font-weight-bold pa-0">{{ dialogTitle }}</v-card-title>
              <v-btn @click="closeDialog" icon="mdi-close" size="small" variant="text"></v-btn>
            </div>
          </v-card>

          <v-card-text style="max-height: 500px; overflow-y: auto">
            <v-form ref="form">
              <v-text-field v-model="areaName" label="Region Code" placeholder="Enter Region Code" variant="outlined"
                maxlength="20" counter class="mb-4 custom_input_field_color"
                :rules="[rules.required, rules.regionCode]"></v-text-field>

              <v-text-field v-model="areaCode" label="Area Code" placeholder="Enter Area Code" variant="outlined"
                maxlength="20" counter class="mb-4 custom_input_field_color"
                :rules="[rules.required, rules.areaCode]"></v-text-field>

              <v-card class="mt-4" elevation="1" rounded="lg" height="250">
                <div class="pa-4 pb-2">
                  <h6 class="font-weight-medium text-grey-darken-4 mb-0" style="font-size: 0.9rem;">Area Name</h6>
                </div>
                <v-divider></v-divider>
                <Editor aria-placeholder="Type Here..." v-model="valueHtml" :defaultConfig="editorConfig" :mode="mode"
                  @onCreated="handleCreated" @onChange="handleEditorChange" />
              </v-card>

              <div class="text-caption text-right mt-1">Word Count: {{ charCount }}/300</div>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions class="px-6 py-4 justify-end">
            <v-btn color="#800080" size="large" variant="flat" class="mr-3" @click="saveArea">
              {{ dialogButtonText }}
            </v-btn>
            <v-btn size="large" variant="outlined" @click="closeDialog">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="delete_area_dialog" max-height="500" max-width="500" persistent id="app_product_list">
        <v-card rounded="md">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <div class="d-flex align-center justify-space-between w-100">
                <v-card-title class="text-subtitle-1 font-weight-bold pa-0">DELETE AREA</v-card-title>
                <v-btn @click="delete_area_dialog = false" icon="mdi-close" size="x-small" variant="text"></v-btn>
              </div>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>
                <v-card-title style="color: #bb0000" class="text-body-1 font-weight-medium">
                  <v-icon color="warning" size="small">mdi-alert</v-icon>
                  &nbsp;&nbsp;Do you want to delete this area?
                </v-card-title>

                <p style="opacity: 0.7" class="text-body-2 mt-4 text-justify">
                  This area will be deleted immediately. Once deleted, it can no longer be used for any purpose.
                </p>

                <v-divider class="mt-4"></v-divider>

                <div class="float-end mt-4">
                  <v-btn class="custom_btn_primary" @click="deleteArea" color="error">DELETE</v-btn>
                  <v-btn class="ml-3 custom_btn_secondary" @click="delete_area_dialog = false">CANCEL</v-btn>
                </div>
              </v-card-item>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="deleteAllDialog" id="app_area_delete_all" max-height="500" max-width="500" persistent>
        <v-card rounded="md">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <div class="d-flex align-center justify-space-between w-100">
                <v-card-title class="text-subtitle-1 font-weight-bold pa-0">DELETE ALL DATA</v-card-title>
                <v-btn @click="deleteAllDialog = false" icon="mdi-close" size="x-small" variant="text"></v-btn>
              </div>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>
                <v-card-title style="color: #BB0000" class="text-body-1 font-weight-medium">
                  <v-icon color="warning" size="small">mdi-alert</v-icon>
                  &nbsp;&nbsp;Do you want to delete all records?
                </v-card-title>

                <p style="opacity: 0.7" class="text-body-2 mt-4 text-justify">
                  All currently filtered records will be deleted immediately. Once deleted, they can no longer be used for any purpose.
                </p>

                <v-divider class="mt-4"></v-divider>

                <div class="float-end mt-4">
                  <v-btn class="custom_btn_primary" @click="executeDeleteAll" :loading="deleteAllLoading" color="error" style="border-radius: 2px;">DELETE ALL</v-btn>
                  <v-btn class="ml-3 custom_btn_secondary" @click="deleteAllDialog = false" style="border-radius: 2px;">CANCEL</v-btn>
                </div>
              </v-card-item>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="AreaBulkUploadDialog" fullscreen id="app_user_list">
        <v-card flat>
          <v-toolbar style="border-radius: 0px !important;"
            class="custom_fullscreen_dialog_toolbar pr-5 custom_card_box" color="white" dense>
            <v-toolbar-title>
              <v-img class="custom_company_logo_nav" :src="require('@/img/company_logo/logo-dark 2.png')"
                @click="homeReload()"></v-img>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn style="background-color: #800080; color: white;" variant="flat" @click="closeBulkUploadDialog">
              CLOSE
            </v-btn>
          </v-toolbar>

          <v-row class="mt-15 custom_scroll_option_fullscreen_dialog">
            <v-col cols="12" md="12">
              <v-card class="pa-0 mx-6 mt-2 custom_card_box" border="sm opacity-1">
                <template v-slot:prepend>
                  <v-icon>mdi-view-dashboard</v-icon>
                  <v-card-title class="font-weight-bold custom_page_main_sub_title ml-3">UPLOAD
                    SPREADSHEET</v-card-title>
                </template>
              </v-card>

              <v-card class="pa-3 mx-6 custom_card_box mt-4" border="sm opacity-1">
                <v-row class="ma-0">
                  <v-col cols="12" xs="12" sm="12" md="5" lg="5">
                    <v-row>
                      <v-col cols="12">
                        <p class="text-body-1 font-weight-medium mb-4 mt-2 custom_page_component_main_title">
                          Upload your area bulk data set using excel or a csv file.
                        </p>
                      </v-col>
                    </v-row>

                    <v-divider></v-divider>

                    <v-row class="mt-4">
                      <v-col cols="12">
                        <v-file-input ref="fileInput" accept=".csv, .xls, .xlsx" clearable density="comfortable"
                          class="custom_input_field_color" label="Upload your Excel or CSV file *" counter show-size
                          variant="outlined" v-model="selectedFile" @update:modelValue="handleFileUpload"
                          :loading="uploading"></v-file-input>
                      </v-col>
                    </v-row>

                    <v-row class="mt-2">
                      <v-col cols="12">
                        <v-btn :disabled="!selectedFile || uploading" :loading="uploading"
                          style="background-color: #800080; color: white;" size="large" block @click="uploadBulkArea">
                          <v-icon left>mdi-cloud-upload</v-icon>
                          UPLOAD AREAS
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col class="d-flex align-center justify-center" cols="12" sm="12" md="1" lg="1">
                    <v-divider :thickness="2" vertical class="d-none d-md-flex"></v-divider>
                    <v-divider :thickness="2" class="d-flex d-md-none my-4"></v-divider>
                  </v-col>

                  <v-col cols="12" xs="12" sm="12" md="6" lg="6">
                    <v-card class="d-flex align-center justify-space-between pa-4 mt-2" color="#F0F0F0" flat
                      width="100%">
                      <div class="mr-4">
                        <p class="mb-0 text-body-2 font-weight-bold">DOWNLOAD SAMPLE TEMPLATE</p>
                        <p class="text-caption mb-0 mt-1" style="opacity: 0.7">Get started with our pre-formatted
                          template</p>
                      </div>
                      <v-divider vertical class="mx-4"></v-divider>
                      <v-btn style="background-color: #800080; color: white;" size="large" variant="flat"
                        @click="downloadTemplate">
                        <v-icon left>mdi-download</v-icon>
                        DOWNLOAD
                      </v-btn>
                    </v-card>

                    <v-card class="mt-4" color="#F5F5F5" flat>
                      <v-card-text>
                        <p class="font-weight-bold custom_page_component_main_title mb-3">File Format Requirements:</p>
                        <v-list density="compact" bg-color="transparent">
                          <v-list-item>
                            <template v-slot:prepend>
                              <v-icon color="#800080" size="small">mdi-check-circle</v-icon>
                            </template>
                            <v-list-item-title class="text-body-2">Accepted formats: .csv, .xls,
                              .xlsx</v-list-item-title>
                          </v-list-item>
                          <v-list-item>
                            <template v-slot:prepend>
                              <v-icon color="#800080" size="small">mdi-check-circle</v-icon>
                            </template>
                            <v-list-item-title class="text-body-2">Maximum file size: 5MB</v-list-item-title>
                          </v-list-item>
                          <v-list-item>
                            <template v-slot:prepend>
                              <v-icon color="#800080" size="small">mdi-check-circle</v-icon>
                            </template>
                            <v-list-item-title class="text-body-2">Required columns: region_code, area_code (exact
                              names)</v-list-item-title>
                          </v-list-item>
                          <v-list-item>
                            <template v-slot:prepend>
                              <v-icon color="#800080" size="small">mdi-check-circle</v-icon>
                            </template>
                            <v-list-item-title class="text-body-2">Optional column: description</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-card v-if="bulkItems.length > 0" flat class="mt-6">
                  <v-card-text>
                    <v-divider class="mb-4"></v-divider>
                    <div class="d-flex justify-space-between align-center mb-4">
                      <div>
                        <h3 class="custom_page_component_main_title">Preview Uploaded Data</h3>
                        <p class="text-caption mt-1" style="opacity: 0.7">Total items: {{ bulkItems.length }}</p>
                      </div>
                      <v-btn size="small" color="error" variant="text" @click="clearPreview">
                        <v-icon left size="small">mdi-close</v-icon> Clear Preview
                      </v-btn>
                    </div>
                    <v-data-table :headers="bulkHeaders" :items="bulkItems" :items-per-page="5"
                      class="custom_table_normal">
                      <template v-slot:[`item.status`]="{ item }">
                        <v-chip :color="item.status === 'Valid' ? 'green' : 'warning'" size="small" variant="tonal"
                          class="font-weight-medium">
                          {{ item.status }}
                        </v-chip>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-dialog>

      <!-- Bulk Upload Confirmation Dialog -->
      <v-dialog v-model="bulkUploadConfirmDialog" max-height="500" max-width="500" persistent>
        <v-card rounded="md">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <div class="d-flex align-center justify-space-between w-100">
                <v-card-title class="text-subtitle-1 font-weight-bold pa-0">CONFIRM BULK UPLOAD</v-card-title>
                <v-btn @click="bulkUploadConfirmDialog = false" icon="mdi-close" size="x-small" variant="text"></v-btn>
              </div>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>
                <v-card-title style="color: #800080" class="text-body-1 font-weight-medium">
                  <v-icon color="info" size="small">mdi-information</v-icon>
                  &nbsp;&nbsp;Confirm Upload
                </v-card-title>

                <p style="opacity: 0.7" class="text-body-2 mt-4 text-justify">
                  You are about to upload {{ bulkUploadItemsCount }} areas. Do you want to continue?
                </p>

                <v-divider class="mt-4"></v-divider>

                <div class="float-end mt-4">
                  <v-btn class="custom_btn_primary" @click="confirmBulkUpload" color="primary" style="background-color: #800080; border-radius: 2px;">CONFIRM</v-btn>
                  <v-btn class="ml-3 custom_btn_secondary" @click="bulkUploadConfirmDialog = false" style="border-radius: 2px;">CANCEL</v-btn>
                </div>
              </v-card-item>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackAlert.snackbar" :color="snackAlert.snackColor" location="top right" timeout="3000">
        {{ snackAlert.snackMessage }}
      </v-snackbar>
    </v-container>
  </v-app>
</template>

<script>
import { Editor } from "@wangeditor/editor-for-vue";
import { onBeforeUnmount, onMounted, ref, shallowRef } from "vue";
import axios from "axios";
import { getPermissions } from "@/plugins/permissions";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import DOMPurify from "dompurify";
import "jspdf-autotable";

export default {
  components: {
    Editor,
  },

  setup() {
    const editorRef = shallowRef();
    const valueHtml = ref("<p></p>");
    const mode = "simple";

    onMounted(() => {
      setTimeout(() => {
        valueHtml.value = "<p></p>";
      }, 1500);
    });

    const toolbarConfig = {
      toolbarKeys: ["bold", "italic", "underline", "through"],
    };
    const editorConfig = { placeholder: "Type here..." };

    onBeforeUnmount(() => {
      const editor = editorRef.value;
      if (editor == null) return;
      editor.destroy();
    });

    const handleCreated = (editor) => {
      editorRef.value = editor;
    };

    return {
      editorRef,
      mode,
      valueHtml,
      toolbarConfig,
      editorConfig,
      handleCreated,
    };
  },

  name: "AreaList",

  data() {
    return {
      baseUrl: process.env.VUE_APP_API_BASE_URL,
      rules: {
        required: (v) => !!v || "This field is required",
        regionCode: (v) =>
          /^[A-Za-z0-9\s.\-_&',/()]+$/.test(v) ||
          "Only letters, numbers, spaces and special characters (., -, _, &, ', /, (, )) are allowed",
        areaCode: (v) =>
          /^[A-Za-z0-9\s.\-_&',/()]+$/.test(v) ||
          "Only letters, numbers, spaces and special characters (., -, _, &, ', /, (, )) are allowed",
      },
      charCount: 0,
      charLimit: 300,
      permissions: {},
      isAdministrator: false,
      search: "",
      page: 1,
      itemsPerPage: 10,
      dialog: false,
      dialogTitle: "CREATE AREA",
      dialogButtonText: "CREATE",
      areaName: "",
      areaCode: "",
      description: "",
      areas: [],
      delete_area_dialog: false,
      deleteAllDialog: false,
      deleteAllLoading: false,
      selectedAreaId: null,
      editingArea: null,
      AreaBulkUploadDialog: false,
      selectedFile: null,
      uploading: false,
      bulkItems: [],
      bulkUploadConfirmDialog: false,
      bulkUploadItemsCount: 0,
      bulkValidItems: [],
      bulkHeaders: [
        { title: "Region Code", key: "region_code", sortable: true },
        { title: "Area Code", key: "area_code", sortable: true },
        { title: "Description", key: "description", sortable: false },
        { title: "Status", key: "status", sortable: false },
      ],
      snackAlert: {
        snackbar: false,
        snackColor: "",
        snackMessage: "",
      },
      headers: [
        { title: "REGION CODE", key: "region_code", sortable: true },
        { title: "AREA CODE", key: "area_code", sortable: true },
        { title: "AREA NAME", key: "description", sortable: true },
        { title: "ACTIONS", key: "actions", sortable: false },
      ],
    };
  },

  computed: {
    totalAreas() {
      const count = this.filteredAreas.length;
      return count < 10 ? `0${count}` : count.toString();
    },
    filteredAreas() {
      if (!this.search || this.search.trim() === '') {
        return this.areas;
      }
      const searchLower = this.search.toLowerCase().trim();
      return this.areas.filter(area => {
        return (
          (area.region_code && area.region_code.toLowerCase().includes(searchLower)) ||
          (area.area_code && area.area_code.toLowerCase().includes(searchLower)) ||
          (area.description && this.stripHtmlTags(area.description).toLowerCase().includes(searchLower))
        );
      });
    }
  },

  methods: {
    getAuthHeaders() {
      const token = localStorage.getItem("token");
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    },

    getMultipartHeaders() {
      const token = localStorage.getItem("token");
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      };
    },

    checkAdministratorStatus() {
      console.log('=== Checking Administrator Status ===');
      console.log('All localStorage keys:', Object.keys(localStorage));
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);
        console.log(`Key: ${key}, Value preview: ${value ? value.substring(0, 100) : 'null'}`);
        
        if (value && value.includes('ADMINISTRATOR')) {
          console.log(`FOUND ADMINISTRATOR in key: ${key}`);
          
          if (key === 'user') {
            try {
              const parsed = JSON.parse(value);
              console.log('Parsed user data:', parsed);
              if (parsed.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                console.log('Set isAdministrator = true from user.userType');
                return;
              }
              if (parsed.user && parsed.user.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                console.log('Set isAdministrator = true from user.user.userType');
                return;
              }
            } catch (e) {
              console.error('Error parsing user key:', e);
            }
          }
          
          if (key === 'loginResponse') {
            try {
              const parsed = JSON.parse(value);
              console.log('Parsed loginResponse data:', parsed);
              if (parsed.user && parsed.user.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                console.log('Set isAdministrator = true from loginResponse.user.userType');
                return;
              }
              if (parsed.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                console.log('Set isAdministrator = true from loginResponse.userType');
                return;
              }
            } catch (e) {
              console.error('Error parsing loginResponse key:', e);
            }
          }
          
          if (key === 'userType') {
            if (value === 'ADMINISTRATOR') {
              this.isAdministrator = true;
              console.log('Set isAdministrator = true from userType key');
              return;
            }
          }
        }
      }
      
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          const payload = JSON.parse(jsonPayload);
          console.log('JWT Token payload:', payload);
        } catch (e) {
          console.error('Error decoding token:', e);
        }
      }
      
      console.log('isAdministrator final value:', this.isAdministrator);
      console.log('permissions:', this.permissions);
      console.log('=== End Administrator Check ===');
    },

    calculateCharCount(html) {
      if (!html) return 0;
      const text = html.replace(/<[^>]+>/g, "");
      return text.length;
    },

    handleEditorChange() {
      const text = this.valueHtml.replace(/<[^>]+>/g, "");
      if (text.length > this.charLimit) {
        this.showSnackbar(`Maximum limit of ${this.charLimit} characters reached.`, "error");
      }
      this.charCount = this.calculateCharCount(this.valueHtml);
    },

    getSanitizedHTML(html) {
      return DOMPurify.sanitize(html);
    },

    stripHtmlTags(html) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || "";
    },

    showSnackbar(message, color) {
      this.snackAlert.snackbar = true;
      this.snackAlert.snackMessage = message;
      this.snackAlert.snackColor = color;
    },

    async fetchAreas({ userId = null, perPage = -1 } = {}) {
      try {
        const finalUserId = userId || localStorage.getItem("userId") || "";
        const params = new URLSearchParams();
        if (finalUserId) params.append("userId", finalUserId);
        if (perPage === -1) params.append("perPage", 10000);
        else params.append("perPage", perPage);

        const response = await axios.get(`${this.baseUrl}/api/area-list?${params.toString()}`, { headers: this.getAuthHeaders() });

        if (response.data.success) {
          this.areas = response.data.data.data;
        }
      } catch (error) {
        console.error("Error fetching areas:", error);
        this.handleApiError(error);
      }
    },

    openDialog() {
      this.dialogTitle = "CREATE AREA";
      this.dialogButtonText = "CREATE";
      this.areaName = "";
      this.areaCode = "";
      this.valueHtml = "<p></p>";
      this.editingArea = null;
      this.charCount = 0;
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.areaName = "";
      this.areaCode = "";
      this.valueHtml = "<p></p>";
      this.editingArea = null;
      this.charCount = 0;
    },

    editArea(item) {
      this.dialogTitle = "EDIT AREA";
      this.dialogButtonText = "UPDATE";
      this.areaName = item.region_code;
      this.areaCode = item.area_code;
      this.valueHtml = item.description || "<p></p>";
      this.editingArea = item;
      this.charCount = this.calculateCharCount(item.description || "");
      this.dialog = true;
    },

    async saveArea() {
      if (this.charCount > this.charLimit) {
        this.showSnackbar(`Description must be ${this.charLimit} characters or less.`, "error");
        return;
      }

      if (!this.areaName || !this.areaCode) {
        this.showSnackbar("Region Code and Area Code are required!", "error");
        return;
      }

      const userId = localStorage.getItem("userId");
      const areaData = {
        userId: parseInt(userId),
        region_code: this.areaName,
        area_code: this.areaCode,
        description: this.valueHtml || "",
      };

      if (this.editingArea) {
        try {
          const response = await axios.put(`${this.baseUrl}/api/area/${this.editingArea.id}`, areaData, { headers: this.getAuthHeaders() });
          if (response.data.success) {
            this.showSnackbar("Area successfully updated.", "success");
            await this.fetchAreas();
            this.closeDialog();
          } else {
            this.showSnackbar("Failed to update area.", "error");
          }
        } catch (error) {
          console.error("Error updating area:", error);
          this.handleApiError(error);
        }
      } else {
        try {
          const response = await axios.post(`${this.baseUrl}/api/create-area`, areaData, { headers: this.getAuthHeaders() });
          if (response.data.success) {
            this.showSnackbar("Area successfully created.", "success");
            await this.fetchAreas();
            this.closeDialog();
          } else {
            this.showSnackbar("Failed to create area.", "error");
          }
        } catch (error) {
          console.error("Error creating area:", error);
          this.handleApiError(error);
        }
      }
    },

    confirmDelete(item) {
      this.selectedAreaId = item.id;
      this.delete_area_dialog = true;
    },

    async deleteArea() {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) {
          this.showSnackbar("User ID not found.", "error");
          return;
        }

        const response = await axios.delete(`${this.baseUrl}/api/area/${this.selectedAreaId}`, {
          headers: this.getAuthHeaders(),
          data: { userId: parseInt(userId) },
        });

        if (response.data.success) {
          this.showSnackbar("Area deleted successfully.", "success");
          this.delete_area_dialog = false;
          await this.fetchAreas();
        } else {
          this.showSnackbar("Failed to delete area.", "error");
        }
      } catch (error) {
        console.error("Error deleting area:", error);
        this.handleApiError(error);
      }
    },

    confirmDeleteAll() {
      if (this.filteredAreas.length === 0) {
        this.showSnackbar("No areas found to delete.", "warning");
        return;
      }
      this.deleteAllDialog = true;
    },

    async executeDeleteAll() {
      this.deleteAllLoading = true;
      try {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem("userId");
        
        const idsToDelete = this.filteredAreas.map(area => area.id);
        
        const response = await axios.post(`${this.baseUrl}/api/deleteAllAreas`, { 
          ids: idsToDelete,
          userId: parseInt(userId)
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.success) {
          this.areas = this.areas.filter(area => !idsToDelete.includes(area.id));
          this.page = 1;

          this.showSnackbar('All selected areas deleted successfully', 'success');
        } else {
          throw new Error(response.data?.message || 'Failed to delete all areas');
        }
      } catch (error) {
        console.error('Error deleting all areas:', error);
        this.handleApiError(error);
      } finally {
        this.deleteAllLoading = false;
        this.deleteAllDialog = false;
      }
    },

    handleApiError(error) {
      if (error.response) {
        const { status, data } = error.response;
        if (status === 401) {
          this.showSnackbar("Unauthorized. Please log in again.", "error");
        } else if (status === 403) {
          this.showSnackbar("You do not have permission to perform this action.", "error");
        } else if (status === 422 && data.error) {
          const errorMessages = Object.values(data.error).flat().join(" ");
          this.showSnackbar(errorMessages, "error");
        } else if (data.message) {
          this.showSnackbar(data.message, "error");
        } else if (data.error) {
          this.showSnackbar(`Error: ${data.error}`, "error");
        } else {
          this.showSnackbar("An unexpected error occurred.", "error");
        }
      } else if (error.request) {
        this.showSnackbar("Network error. Please check your connection.", "error");
      } else {
        this.showSnackbar("An unexpected error occurred.", "error");
      }
    },

    async exportPDF() {
      const dataToExport = this.filteredAreas;
      
      if (dataToExport.length === 0) {
        this.showSnackbar("No data to export. Please adjust your search criteria.", "warning");
        return;
      }

      const doc = new jsPDF();
      
      // Set title - matching User List style
      doc.setFontSize(16);
      doc.text('AREAS LIST REPORT', 14, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

      // Define columns - keeping all original columns
      const tableColumn = [
        'Region Code',
        'Area Code', 
        'Area Name',
        'Created Date',
        'Updated Date'
      ];
      
      const tableRows = dataToExport.map(item => [
        item.region_code || 'N/A',
        item.area_code || 'N/A',
        this.stripHtmlTags(item.description || 'N/A'),
        item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A',
        item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A'
      ]);

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 40,
        theme: 'grid',
        headStyles: {
          fillColor: [128, 0, 128], // Using #800080 purple - matching User List
          textColor: [255, 255, 255],
          fontSize: 8
        },
        bodyStyles: {
          fontSize: 7
        }
      });

      const fileName = this.search && this.search.trim() !== '' 
        ? `Areas_List_Report_Filtered_${this.search.replace(/[^a-z0-9]/gi, '_')}.pdf`
        : "Areas_List_Report.pdf";
      
      doc.save(fileName);
      this.logDocumentDownload(fileName, "pdf");
    },

    exportExcel() {
      const dataToExport = this.filteredAreas;
      
      if (dataToExport.length === 0) {
        this.showSnackbar("No data to export. Please adjust your search criteria.", "warning");
        return;
      }

      const excelData = dataToExport.map(item => ({
        'Region Code': item.region_code || 'N/A',
        'Area Code': item.area_code || 'N/A',
        'Area Name': this.stripHtmlTags(item.description || 'N/A'),
        'Created Date': item.created_at ? new Date(item.created_at).toLocaleString() : 'N/A',
        'Updated Date': item.updated_at ? new Date(item.updated_at).toLocaleString() : 'N/A'
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 20 },
        { wch: 20 },
        { wch: 40 },
        { wch: 22 },
        { wch: 22 }
      ];
      
      // Style the header row with purple background
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:E1');
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[address]) continue;
        worksheet[address].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "800080" }, patternType: "solid" },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Areas List");

      const fileName = this.search && this.search.trim() !== '' 
        ? `Areas_List_Report_Filtered_${this.search.replace(/[^a-z0-9]/gi, '_')}.xlsx`
        : "Areas_List_Report.xlsx";

      XLSX.writeFile(workbook, fileName);
      this.logDocumentDownload(fileName, "excel");
    },

    logDocumentDownload(fileName, file_type) {
      try {
        const userId = localStorage.getItem("userId");
        axios.post(`${this.baseUrl}/api/log-document-download`, {
          user_id: userId,
          file_type: file_type,
          file_name: fileName,
          entity_area: "Area Management",
        }, { headers: this.getAuthHeaders() }).catch(error => {
          console.error("Error logging download:", error);
        });
      } catch (error) {
        console.error("Error logging download:", error);
      }
    },

    backPageRoute() {
      this.$router.back();
    },

    handleFileUpload(file) {
      if (!file) {
        this.selectedFile = null;
        this.bulkItems = [];
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.showSnackbar("File size exceeds 5MB limit.", "error");
        this.selectedFile = null;
        this.bulkItems = [];
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);

          this.bulkItems = jsonData.map((row, index) => {
            // Support multiple column name variations
            let region_code = row["region_code"] || row["region code"] || row["Region Code"] || row["REGION CODE"] || "";
            let area_code = row["area_code"] || row["area code"] || row["Area Code"] || row["AREA CODE"] || "";
            let description = row["description"] || row["area_name"] || row["area name"] || row["Area Name"] || row["AREA NAME"] || "";
            
            // Convert to string and trim
            region_code = String(region_code).trim();
            area_code = String(area_code).trim();
            description = String(description).trim();
            
            const isValid = region_code && area_code;

            return {
              id: index + 1,
              region_code: region_code,
              area_code: area_code,
              description: description,
              status: isValid ? "Valid" : "Missing Required Fields",
              errors: isValid ? [] : [region_code ? "" : "region_code", area_code ? "" : "area_code"].filter(e => e)
            };
          });

          const validCount = this.bulkItems.filter(item => item.status === "Valid").length;
          const invalidCount = this.bulkItems.length - validCount;

          if (invalidCount > 0) {
            this.showSnackbar(`Loaded ${this.bulkItems.length} areas. Valid: ${validCount}, Invalid: ${invalidCount}. Please check missing required columns.`, "warning");
          } else {
            this.showSnackbar(`Loaded ${this.bulkItems.length} valid areas. Ready to upload.`, "success");
          }
        } catch (error) {
          console.error("Error parsing file:", error);
          this.showSnackbar("Error parsing file. Please check the file format.", "error");
          this.selectedFile = null;
          this.bulkItems = [];
        }
      };
      reader.readAsArrayBuffer(file);
    },

    downloadTemplate() {
      // Create template with EXACT column names as shown in the user's example
      const templateData = [
        ["region_code", "area_code", "area_name"],
        ["ce001", "cea001", "test celata"],
        ["North Region", "NR001", "Northern area description"],
        ["South Region", "SR001", "Southern area description"],
        ["East Region", "ER001", "Eastern area description"],
        ["West Region", "WR001", "Western area description"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(templateData);
      worksheet["!cols"] = [{ wch: 20 }, { wch: 20 }, { wch: 40 }];
      
      // Add styling to header row
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:C1');
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[address]) continue;
        worksheet[address].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "800080" }, patternType: "solid" },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Area Template");
      XLSX.writeFile(workbook, "Area_Bulk_Upload_Template.xlsx");
      this.showSnackbar("Template downloaded successfully. Use 'region_code', 'area_code', and 'area_name' as column headers.", "success");
    },

    async uploadBulkArea() {
      if (!this.selectedFile) {
        this.showSnackbar("Please select a file to upload.", "error");
        return;
      }

      const validItems = this.bulkItems.filter(item => item.status === "Valid");
      if (validItems.length === 0) {
        this.showSnackbar("No valid data to upload. Please check your file has 'region_code' and 'area_code' columns with values.", "error");
        return;
      }

      // Store the count and the valid items for later use
      this.bulkUploadItemsCount = validItems.length;
      this.bulkValidItems = validItems;
      this.bulkUploadConfirmDialog = true;
    },

    async confirmBulkUpload() {
      this.bulkUploadConfirmDialog = false;
      this.uploading = true;

      try {
        const userId = localStorage.getItem("userId");
        if (!userId) {
          this.showSnackbar("User ID not found. Please log in again.", "error");
          this.uploading = false;
          return;
        }

        const formData = new FormData();
        formData.append("userId", userId);
        formData.append("file", this.selectedFile);

        const response = await axios.post(`${this.baseUrl}/api/areas/bulk-upload`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data.success) {
          this.showSnackbar(response.data.message || `Successfully uploaded ${this.bulkUploadItemsCount} areas.`, "success");
          await this.fetchAreas();
          this.closeBulkUploadDialog();
        } else {
          if (response.data.errors && response.data.errors.length > 0) {
            const errorMessage = `${response.data.message}. Failed rows: ${response.data.failed_rows}`;
            this.showSnackbar(errorMessage, "warning");
            console.error("Upload errors:", response.data.errors);
          } else {
            this.showSnackbar(response.data.message || "Bulk upload failed.", "error");
          }
        }
      } catch (error) {
        console.error("Error in bulk upload:", error);
        if (error.response) {
          const { data } = error.response;
          console.log("Server error details:", data);

          if (data.errors && data.errors.length > 0) {
            const firstErrors = data.errors.slice(0, 3);
            const errorMsg = firstErrors.map(e => `Row ${e.row}: ${e.errors.join(", ")}`).join("; ");
            this.showSnackbar(`Upload failed: ${errorMsg}`, "error");
          } else {
            this.showSnackbar(data.message || data.error || "Bulk upload failed. Please check your file format.", "error");
          }
        } else if (error.request) {
          this.showSnackbar("Network error. Please check your connection.", "error");
        } else {
          this.showSnackbar("An unexpected error occurred.", "error");
        }
      } finally {
        this.uploading = false;
      }
    },

    closeBulkUploadDialog() {
      this.AreaBulkUploadDialog = false;
      this.selectedFile = null;
      this.bulkItems = [];
      this.uploading = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset();
      }
    },

    clearPreview() {
      this.bulkItems = [];
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset();
      }
    },

    homeReload() {
      this.$router.push("/");
    },
  },

  mounted() {
    const dashboardId = 4;
    this.permissions = getPermissions(dashboardId);
    this.checkAdministratorStatus();
    this.fetchAreas();

    if (this.$route.query.openBulkUpload === 'true') {
      this.AreaBulkUploadDialog = true;
    }
  },
};
</script>

<style lang="scss" scoped>
@use "../../styles/user_management/users_list.scss" as *;
</style>